// src/features/analytics/useAnalyticsMeta.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi } from './AnalyticsApi';
import { ANALYTICS_QUERY_KEYS } from '@/lib/constants/analytics';
import { utcDateToSeconds } from '@/shared/utils/utcRange';
import type { AnalyticsMeta } from './types';

/** Resolved event-window features (dashboard ADR-009): false until the gateway declares them. */
export interface AnalyticsFeatures {
  /** Hourly/window/broadcast routes are allowlisted (the per-stream page has data). */
  hourly: boolean;
  /** The `tz` param is allowlisted (local calendar days on Overview/Content). */
  tz: boolean;
}

export const NO_ANALYTICS_FEATURES: AnalyticsFeatures = { hourly: false, tz: false };

export interface UseAnalyticsMetaResult {
  meta: AnalyticsMeta | null;
  /** Event-window feature flags from /meta; all false while loading or on an older gateway. */
  features: AnalyticsFeatures;
  /**
   * UTC midnight of the product-window start T, or null when no floor applies
   * (customerStartDate stays null through all of Phase 5 — the floor is
   * dormant but wired; Phase 6 activates it via configuration alone).
   */
  productWindowFloorSeconds: number | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Fetch /v1/analytics/meta once per session (gateway-local; account-independent). */
export function useAnalyticsMeta(): UseAnalyticsMetaResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.meta(),
    queryFn: () => AnalyticsApi.getMeta(),
    staleTime: Infinity,
  });

  const meta = query.data ?? null;
  const productWindowFloorSeconds = meta?.customerStartDate
    ? utcDateToSeconds(meta.customerStartDate)
    : null;

  const features: AnalyticsFeatures = {
    hourly: meta?.features?.hourly === true,
    tz: meta?.features?.tz === true,
  };

  return {
    meta,
    features,
    productWindowFloorSeconds,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
